import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { campaignsApi, type Deployment } from '../api/campaignsApi';
import type { GooglePMaxData } from './types';
import { EditableList, CollapsibleSection, MonthlyBudgetField } from './ReviewComponents';
import { AssetSlotUploader, type SlotSpec } from '@/modules/media/components/AssetSlotUploader';
import { useManagementMode } from './useManagementMode';
import styles from './ReviewPanel.module.css';
import patterns from '@/shared/ui/styles/patterns.module.css';

const EMPTY_STRINGS: string[] = [];

interface Props {
  deployment: Deployment;
  campaignId: string;
  locationId: string;
  practiceId?: string;
  onSave: (platformData: GooglePMaxData) => void;
  isSaving: boolean;
}

function slotRole(rawSlot: string): string {
  return rawSlot.trim().toLowerCase().replace(/\s+/g, '_');
}

function slotLabel(rawSlot: string): string {
  return rawSlot
    .trim()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function GooglePMaxReview({
  deployment,
  campaignId,
  locationId,
  practiceId,
  onSave,
  isSaving,
}: Props) {
  const data = deployment.platformData as GooglePMaxData;
  const queryClient = useQueryClient();
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['campaigns', practiceId ?? 'me', campaignId] });

  const attachMutation = useMutation({
    mutationFn: (payload: { mediaAssetId: string; role: string }) =>
      campaignsApi.attachAsset(campaignId, deployment.id, payload, practiceId),
    onSuccess: invalidate,
  });

  const detachMutation = useMutation({
    mutationFn: ({ mediaAssetId, role }: { mediaAssetId: string; role: string }) =>
      campaignsApi.detachAsset(campaignId, deployment.id, mediaAssetId, role, practiceId),
    onSuccess: invalidate,
  });
  const isAutopilot = useManagementMode() === 'autopilot';

  if (!data) return <p style={{ color: 'var(--gray-500)' }}>No data generated yet.</p>;

  const update = (field: keyof GooglePMaxData, value: GooglePMaxData[keyof GooglePMaxData]) =>
    onSave({ ...data, [field]: value });

  // PMax allows multiple assets per type (e.g., several MARKETING_IMAGE), so
  // append a per-type index to make each slot's role unique. Show #N in the
  // label only when a type repeats.
  const rawSuggestions = data.imageSuggestions ?? [];
  const typeCounts = rawSuggestions.reduce<Record<string, number>>((acc, s) => {
    const t = slotRole(s.slot);
    acc[t] = (acc[t] ?? 0) + 1;
    return acc;
  }, {});
  const typeIndexes: Record<string, number> = {};
  const slots: SlotSpec[] = rawSuggestions.map((s) => {
    const type = slotRole(s.slot);
    const idx = typeIndexes[type] ?? 0;
    typeIndexes[type] = idx + 1;
    const label = typeCounts[type] > 1 ? `${slotLabel(s.slot)} #${idx + 1}` : slotLabel(s.slot);
    return {
      role: `${type}_${idx}`,
      label,
      description: s.description,
      aspectRatio: s.aspectRatio,
    };
  });
  const attachments = deployment.assets ?? [];
  const isLive = deployment.deployStatus === 'live';

  const headlines = data.headlines ?? EMPTY_STRINGS;
  const longHeadlines = data.longHeadlines ?? EMPTY_STRINGS;
  const descriptions = data.descriptions ?? EMPTY_STRINGS;

  return (
    <div className={styles.panel}>
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Campaign Overview</h3>
        <div className={styles.metaRow}>
          <MonthlyBudgetField
            dailyBudget={data.dailyBudget}
            onSave={(nextDaily) => update('dailyBudget', nextDaily)}
            disabled={isLive || isAutopilot}
          />
          <div className={styles.metaStat}>
            <span className={styles.metaValue}>{data.bidStrategy?.replace(/_/g, ' ')}</span>
            <span className={styles.metaLabel}>Bid strategy</span>
          </div>
          <div className={styles.metaStat}>
            <span className={styles.metaValue}>{data.businessName}</span>
            <span className={styles.metaLabel}>Business name</span>
          </div>
        </div>
      </section>

      <CollapsibleSection
        title="Headlines"
        meta={`${headlines.length}/15 · max 30 chars`}
        defaultOpen
      >
        <EditableList
          items={headlines}
          maxChar={30}
          onUpdate={(v) => update('headlines', v)}
          disabled={isLive || isAutopilot}
        />
      </CollapsibleSection>

      <CollapsibleSection
        title="Long Headlines"
        meta={`${longHeadlines.length}/5 · max 90 chars`}
        defaultOpen
      >
        <EditableList
          items={longHeadlines}
          maxChar={90}
          onUpdate={(v) => update('longHeadlines', v)}
          disabled={isLive || isAutopilot}
        />
      </CollapsibleSection>

      <CollapsibleSection
        title="Descriptions"
        meta={`${descriptions.length}/5 · max 90 chars`}
        defaultOpen
      >
        <EditableList
          items={descriptions}
          maxChar={90}
          onUpdate={(v) => update('descriptions', v)}
          disabled={isLive || isAutopilot}
        />
      </CollapsibleSection>

      <CollapsibleSection title="Sitelinks" meta={`${(data.sitelinks ?? []).length}`}>
        <div className={styles.sitelinkList}>
          {(data.sitelinks ?? []).map((sl, i) => (
            <div key={i} className={styles.sitelinkCard}>
              <div className={styles.sitelinkText}>{sl.text}</div>
              <div className={styles.sitelinkDesc}>{sl.description1}</div>
              <div className={styles.sitelinkDesc}>{sl.description2}</div>
            </div>
          ))}
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Audience Signals">
        <div className={patterns.fieldStack}>
          {data.audienceSignals?.customAudiences?.length > 0 && (
            <div className={patterns.field}>
              <span className={patterns.fieldLabel}>Custom audiences</span>
              <div className={styles.tagList}>
                {data.audienceSignals.customAudiences.map((a, i) => (
                  <span key={i} className={styles.calloutTag}>
                    {a}
                  </span>
                ))}
              </div>
            </div>
          )}
          {data.audienceSignals?.interests?.length > 0 && (
            <div className={patterns.field}>
              <span className={patterns.fieldLabel}>Interests</span>
              <div className={styles.tagList}>
                {data.audienceSignals.interests.map((a, i) => (
                  <span key={i} className={styles.calloutTag}>
                    {a}
                  </span>
                ))}
              </div>
            </div>
          )}
          {data.audienceSignals?.demographics && (
            <p className={styles.audienceDemographics}>{data.audienceSignals.demographics}</p>
          )}
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title="Assets"
        meta={`${attachments.length}/${slots.length} filled`}
        defaultOpen
      >
        <div className={patterns.field}>
          <span className={patterns.fieldLabel}>
            Drop or click to upload an image for each slot · JPG, PNG, or WebP up to 10MB
          </span>
          <div className={styles.imageSuggestions}>
            {slots.map((slot) => (
              <AssetSlotUploader
                key={slot.role}
                locationId={locationId}
                slot={slot}
                attachment={attachments.find((a) => a.role === slot.role)}
                disabled={
                  isLive || isAutopilot || attachMutation.isPending || detachMutation.isPending
                }
                onAttach={async (mediaAssetId, role) => {
                  await attachMutation.mutateAsync({ mediaAssetId, role });
                }}
                onDetach={async (mediaAssetId, role) => {
                  await detachMutation.mutateAsync({ mediaAssetId, role });
                }}
              />
            ))}
          </div>
        </div>
      </CollapsibleSection>

      {isSaving && <p className={styles.savingNote}>Saving…</p>}
    </div>
  );
}
